import { FC, useCallback, useEffect, useState } from "react";
import { reverifyConfirm, reverifyRequest } from "../../core/_requests";
import "../style.css";

interface Props {
  onSuccess: () => void;
}

type Phase = "loading" | "code" | "blocked" | "telegram" | "error";

export const UserReverifyModalBody: FC<Props> = ({ onSuccess }) => {
  const [phase, setPhase] = useState<Phase>("loading");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Запрашиваем у сервера отправку одноразового кода в Telegram.
  const requestCode = useCallback(async () => {
    setPhase("loading");
    setError(null);
    try {
      const res = await reverifyRequest();
      if (res.status === 200) {
        setPhase("code");
      } else if (
        res.status === 409 &&
        res.data?.code === "reverify_telegram_required"
      ) {
        setPhase("telegram");
      } else if (res.status === 429) {
        setPhase("blocked");
      } else {
        setPhase("error");
      }
    } catch {
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    requestCode();
  }, [requestCode]);

  const submit = async () => {
    const value = code.trim();
    if (!value || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await reverifyConfirm(value);
      if (res.status === 200 && res.data?.verified) {
        onSuccess();
      } else if (res.status === 409 && res.data?.code === "reverify_invalid") {
        setError("Неверный или просроченный код. Попробуйте ещё раз.");
        setCode("");
      } else if (res.status === 429) {
        setPhase("blocked");
      } else {
        setError("Не удалось подтвердить код. Попробуйте ещё раз.");
      }
    } catch {
      setError("Ошибка сети. Попробуйте ещё раз.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "loading") {
    return (
      <div className="d-flex flex-center py-10">
        <span className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (phase === "blocked") {
    return (
      <div className="fs-6 mb-5 text-center">
        <p>
          Слишком много неверных попыток. Повторная верификация временно
          заблокирована — попробуйте примерно через 15 минут.
        </p>
      </div>
    );
  }

  if (phase === "telegram") {
    return (
      <div className="fs-6 mb-5 text-center">
        <p>
          К вашему аккаунту не привязан Telegram, поэтому код отправить не
          удалось.
        </p>
        <p>
          Обратитесь в тех. поддержку в боте <b>@Gross_escrow</b>, чтобы
          привязать Telegram. Другого способа подтвердить доступ нет.
        </p>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="fs-6 mb-5 text-center">
        <p>
          Не удалось запросить код. Проверьте соединение и попробуйте снова.
        </p>
        <div className="d-flex flex-center">
          <button
            type="button"
            className="btn btn-primary"
            onClick={requestCode}
          >
            Повторить
          </button>
        </div>
      </div>
    );
  }

  // phase === "code"
  return (
    <>
      <div className="fs-6 mb-8 text-center">
        <p>
          Для продолжения работы подтвердите вход. Мы отправили одноразовый код
          в ваш Telegram (<b>@Gross_escrow</b>). Код действителен 5 минут.
        </p>
      </div>

      <div className="mb-5">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          className="form-control form-control-lg text-center"
          placeholder="Код из Telegram"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
      </div>

      {error && (
        <div className="alert alert-danger text-center py-3">{error}</div>
      )}

      <div className="d-flex flex-center flex-column">
        <button
          type="button"
          className="btn btn-primary w-100 mb-3"
          onClick={submit}
          disabled={submitting || !code.trim()}
        >
          {submitting ? (
            <span className="indicator-progress d-block">
              Проверяем...{" "}
              <span className="spinner-border spinner-border-sm align-middle ms-2" />
            </span>
          ) : (
            "Подтвердить"
          )}
        </button>

        <button
          type="button"
          className="btn btn-link"
          onClick={requestCode}
          disabled={submitting}
        >
          Отправить код повторно
        </button>
      </div>
    </>
  );
};
